// The one place a report's numbers are decided.
//
// Every figure a report shows comes from `evaluation_details`, the per-question
// array `generate-report` writes server-side. Nothing here reads
// `test_sessions.answers`, the copy the browser saves, because that copy is
// lossy: measured across all 185 reports it disagreed with the server on 1,893
// question rows, including 17 where the server said "correct" and the browser
// said "failed". A report that renders one and totals the other can contradict
// itself on screen, which is exactly what happened.
//
// So: this module takes evaluation_details, and only evaluation_details, and
// derives the whole page from it. If a figure cannot be derived here, the page
// does not show it.

export type QuestionType =
  | "mcq" | "coding-mcq" | "fill_blank" | "coding"
  | "essay" | "system_design" | string;

export interface EvaluationDetail {
  question_id: string;
  question_type: QuestionType;
  skill?: string | null;
  difficulty?: string | null;
  points_awarded?: number | null;
  /** Alias used by older rows; generate-report writes points_awarded today. */
  points_earned?: number | null;
  max_points?: number | null;
  is_correct?: boolean | null;
  answered?: boolean | null;
  time_taken?: number | null;
  code_review?: CodeReview | null;
}

export interface CodeReview {
  overall_score?: number | null;
  verdict?: string | null;
  summary?: string | null;
  bugs?: string[] | null;
  improvements?: string[] | null;
  good_parts?: string[] | null;
  lines_added?: number | null;
  lines_changed?: number | null;
}

/** Answers whose correctness a machine can check outright. */
const CLOSED_FORM = new Set(["mcq", "coding-mcq", "fill_blank", "coding"]);

// Number(null) is 0 and Number("") is 0, so an absent value would otherwise
// read as a real zero: a missing pass mark became 0 (everyone passes) and a
// missing duration became "0s". Absence has to stay absent.
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * evaluation_details as stored can be a JSON string, an array, or absent.
 * Anything that is not a usable array comes back as null rather than [] so
 * callers can tell "no evaluation" apart from "evaluated, nothing in it".
 */
export function parseEvaluation(raw: unknown): EvaluationDetail[] | null {
  let v = raw;
  if (typeof v === "string") {
    try { v = JSON.parse(v); } catch { return null; }
  }
  if (!Array.isArray(v) || v.length === 0) return null;
  const rows = v.filter(
    (d): d is EvaluationDetail => !!d && typeof d === "object" && typeof (d as any).question_id === "string",
  );
  return rows.length ? rows : null;
}

/**
 * Points for one question.
 *
 * Older and seeded rows carry `is_correct` but no `points_awarded`. For a
 * question that is simply right or wrong that is recoverable: correct earns the
 * question's full marks. It is NOT recoverable for anything rubric-graded,
 * where a false `is_correct` can still have earned partial credit, so those
 * stay at whatever was recorded and the report is flagged as derived.
 */
const BINARY = new Set(["mcq", "coding-mcq", "fill_blank", "true_false"]);

/** The marks recorded for a question, under either field name it has used. */
function recordedPoints(d: EvaluationDetail): number | null {
  return num(d.points_awarded) ?? num(d.points_earned);
}

function pointsFor(d: EvaluationDetail): { points: number; derived: boolean } {
  const p = recordedPoints(d);
  if (p !== null) return { points: p, derived: false };
  const max = num(d.max_points);
  if (max !== null && typeof d.is_correct === "boolean" && BINARY.has(String(d.question_type))) {
    return { points: d.is_correct ? max : 0, derived: true };
  }
  return { points: 0, derived: max !== null };
}

export interface Bucket { label: string; points: number; max: number; count: number; pct: number }

const pct = (points: number, max: number) => (max > 0 ? (points / max) * 100 : 0);

function bucket(rows: EvaluationDetail[], key: (d: EvaluationDetail) => string | null): Bucket[] {
  const acc = new Map<string, Bucket>();
  for (const d of rows) {
    const label = key(d);
    if (!label) continue;
    const max = num(d.max_points) ?? 0;
    const pts = pointsFor(d).points;
    const b = acc.get(label) ?? { label, points: 0, max: 0, count: 0, pct: 0 };
    b.points += pts; b.max += max; b.count += 1;
    acc.set(label, b);
  }
  const out = [...acc.values()];
  for (const b of out) b.pct = pct(b.points, b.max);
  // Strongest first: a reader scanning the list should meet the strengths first.
  return out.sort((a, b) => b.pct - a.pct || b.max - a.max);
}

export interface ReportModel {
  totalPoints: number;
  maxPoints: number;
  percentage: number;
  passMark: number;
  passed: boolean;
  /** Marks still needed to reach the pass mark; 0 once the mark is met. */
  pointsShort: number;
  /** Marks clear of the pass mark; 0 when the mark was not met. */
  pointsClear: number;
  questionCount: number;
  answeredCount: number;
  correctCount: number;
  skills: Bucket[];
  difficulty: Bucket[];
  /** Machine-checkable answers vs answers that had to be written out. */
  closedForm: Bucket | null;
  openEnded: Bucket | null;
  secondsTaken: number | null;
  /** True when any question's points had to be inferred from is_correct. */
  pointsDerived: boolean;
  rows: EvaluationDetail[];
}

const DIFFICULTY_ORDER = ["easy", "medium", "hard"];

export function buildReportModel(
  evaluation: EvaluationDetail[],
  opts: { passMark?: number | null } = {},
): ReportModel {
  const rows = evaluation;
  let totalPoints = 0, maxPoints = 0, correctCount = 0, answeredCount = 0, seconds = 0, timed = 0;
  let pointsDerived = false;

  for (const d of rows) {
    const p = pointsFor(d);
    if (p.derived) pointsDerived = true;
    totalPoints += p.points;
    maxPoints += num(d.max_points) ?? 0;
    if (d.is_correct === true) correctCount += 1;
    // `answered` is the server's own flag; fall back to "scored at all" only
    // when it is missing, never to a guess about the answer's content.
    if (d.answered === true || (d.answered == null && (recordedPoints(d) != null || d.is_correct != null))) answeredCount += 1;
    const t = num(d.time_taken);
    if (t != null && t >= 0) { seconds += t; timed += 1; }
  }

  const passMark = num(opts.passMark) ?? 60;
  const percentage = Math.round(pct(totalPoints, maxPoints) * 10) / 10;

  const difficulty = bucket(rows, (d) => (d.difficulty ? String(d.difficulty).toLowerCase() : null))
    .sort((a, b) => DIFFICULTY_ORDER.indexOf(a.label) - DIFFICULTY_ORDER.indexOf(b.label));

  const closedRows = rows.filter((d) => CLOSED_FORM.has(String(d.question_type)));
  const openRows = rows.filter((d) => !CLOSED_FORM.has(String(d.question_type)));
  const asBucket = (label: string, rs: EvaluationDetail[]): Bucket | null => {
    if (!rs.length) return null;
    const points = rs.reduce((s, d) => s + pointsFor(d).points, 0);
    const max = rs.reduce((s, d) => s + (num(d.max_points) ?? 0), 0);
    return { label, points, max, count: rs.length, pct: pct(points, max) };
  };

  return {
    totalPoints,
    maxPoints,
    percentage,
    passMark,
    passed: percentage >= passMark,
    // Both sides in marks, so "clear by" and "short by" are the same unit and
    // agree with the "N marks short" the recommendation gives.
    pointsShort: Math.max(0, Math.round(((passMark / 100) * maxPoints - totalPoints) * 10) / 10),
    pointsClear: Math.max(0, Math.round((totalPoints - (passMark / 100) * maxPoints) * 10) / 10),
    questionCount: rows.length,
    answeredCount,
    correctCount,
    skills: bucket(rows, (d) => (d.skill ? String(d.skill) : null)),
    difficulty,
    closedForm: asBucket("Closed-form", closedRows),
    openEnded: asBucket("Open-ended", openRows),
    secondsTaken: timed > 0 ? seconds : null,
    pointsDerived,
    rows,
  };
}

/**
 * Does the model's arithmetic hold together?
 *
 * Returns the problems found, empty when everything reconciles. Callers use
 * this to refuse to render a self-contradicting report rather than shipping one
 * to a client.
 */
export function checkModel(m: ReportModel): string[] {
  const problems: string[] = [];
  const skillSum = m.skills.reduce((s, b) => s + b.points, 0);
  const skillMax = m.skills.reduce((s, b) => s + b.max, 0);

  if (m.maxPoints <= 0) problems.push("no question in this report carries any points");
  if (m.totalPoints > m.maxPoints) problems.push(`awarded ${m.totalPoints} of a possible ${m.maxPoints}`);
  if (m.totalPoints < 0) problems.push("negative total");
  // Skills only reconcile when every question carries one, which is the normal
  // case; an unlabelled question is a data gap worth surfacing, not hiding.
  if (m.skills.length && skillSum !== m.totalPoints)
    problems.push(`skills total ${skillSum} but the report totals ${m.totalPoints}`);
  if (m.skills.length && skillMax !== m.maxPoints)
    problems.push(`skills allow ${skillMax} points but the report allows ${m.maxPoints}`);
  if (m.correctCount > m.questionCount) problems.push("more correct answers than questions");
  if (m.answeredCount > m.questionCount) problems.push("more answers than questions");

  // Every question missing its points means the evaluation was never scored;
  // rendering that as 0% would tell a client a candidate failed when in truth
  // nothing was measured.
  if (m.rows.length && m.rows.every((d) => recordedPoints(d) === null && d.is_correct == null))
    problems.push("this evaluation records no scores");

  for (const d of m.rows) {
    const p = recordedPoints(d), mx = num(d.max_points);
    if (p != null && mx != null && p > mx)
      problems.push(`a question awarded ${p} of ${mx}`);
  }
  return problems;
}

/** Seconds as "9m 54s" / "45s", for a reader rather than a log. */
export function formatDuration(seconds: number | null | undefined): string {
  const s = num(seconds);
  if (s == null || s < 0) return "";
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m ? `${m}m ${r}s` : `${r}s`;
}

// ---------------------------------------------------------------------------
// Turning the numbers into the sentence a hiring manager actually needs.
//
// This is deterministic on purpose. The headline a client reads has to follow
// from the scored record, so the same result always produces the same advice
// and we can defend any of it by pointing at the points.
// ---------------------------------------------------------------------------

export type Verdict = "clear-pass" | "pass" | "borderline" | "clear-fail" | "no-signal";

export interface Narrative {
  verdict: Verdict;
  /** One line: the decision. */
  headline: string;
  /** Two or three sentences of why, in plain words. */
  detail: string;
  /** When this candidate is worth hiring, and when they are not. */
  fitFor: string | null;
  fitAgainst: string | null;
  /** The cheapest way to resolve what the test could not measure. */
  nextStep: string | null;
}

/** Within this many percentage points of the mark counts as borderline. */
const BORDERLINE_BAND = 10;

// Reports are read by clients about real people whose pronouns we do not
// collect, so the copy stays in they/them throughout. Guessing from a name
// misgenders someone in a document that decides whether they get hired.
export function deriveNarrative(m: ReportModel, firstName = "This candidate"): Narrative {
  const who = firstName || "This candidate";
  const gap = Math.round((m.percentage - m.passMark) * 10) / 10;
  const closed = m.closedForm;
  const open = m.openEnded;

  // A split profile is the most decision-useful shape there is: strong where an
  // answer can be checked, weak where it has to be argued (or the reverse).
  const hasBoth = !!closed && !!open && closed.max > 0 && open.max > 0;
  const codeStrong = hasBoth && closed!.pct >= 75;
  const wordsWeak = hasBoth && open!.pct <= 30;
  const codeWeak = hasBoth && closed!.pct <= 40;
  const wordsStrong = hasBoth && open!.pct >= 60;

  if (checkModel(m).includes("this evaluation records no scores")) {
    return {
      verdict: "no-signal",
      headline: "This assessment was not scored.",
      detail: "No marks were recorded against any question, so there is nothing to judge here. The assessment needs regenerating before it can be used for a decision.",
      fitFor: null, fitAgainst: null, nextStep: null,
    };
  }

  const strongest = m.skills[0];
  const weakest = m.skills.length > 1 ? m.skills[m.skills.length - 1] : null;

  let verdict: Verdict;
  if (m.passed && gap >= BORDERLINE_BAND) verdict = "clear-pass";
  else if (m.passed) verdict = "pass";
  else if (-gap <= BORDERLINE_BAND) verdict = "borderline";
  else verdict = "clear-fail";

  let headline: string;
  if (codeStrong && wordsWeak) {
    headline = m.passed
      ? `${who} can build, but has not shown they can explain their thinking.`
      : `Worth a short call. ${who} can build, but has not shown they can explain their thinking.`;
  } else if (wordsStrong && codeWeak) {
    headline = `${who} reasons well but struggled with the hands-on work.`;
  } else if (verdict === "clear-pass") {
    headline = `Strong result. ${who} cleared the bar comfortably.`;
  } else if (verdict === "pass") {
    headline = `${who} passed, but only just.`;
  } else if (verdict === "borderline") {
    headline = `Borderline. ${who} missed the mark by a small margin.`;
  } else {
    headline = `${who} is some way below the bar for this role.`;
  }

  // "but only" implies a contrast, so it may only be used when there is one.
  // Two low halves is not a split profile, it is a uniformly weak result.
  const c = closed && closed.max > 0 ? Math.round(closed.pct) : null;
  const o = open && open.max > 0 ? Math.round(open.pct) : null;
  let split = "";
  if (c !== null && o !== null) {
    const contrast = Math.abs(c - o) >= 25;
    if (contrast && c > o) {
      split = `They scored ${c}% on the questions with a checkable answer${c >= 75 ? ", including work that was run and tested" : ""}, but only ${o}% on the questions asking them to write out how they would solve a problem. `;
    } else if (contrast) {
      split = `They scored ${o}% on the written questions but only ${c}% on the questions with a checkable answer. `;
    } else {
      split = `They scored ${c}% on the checkable questions and ${o}% on the written ones. `;
    }
  } else if (c !== null) {
    split = `They scored ${c}% across the assessment. `;
  }

  const margin = m.passed
    ? `That is ${Math.abs(gap)} percentage points clear of the ${m.passMark}% mark you set.`
    : `That leaves them ${m.pointsShort} mark${m.pointsShort === 1 ? "" : "s"} short of the ${m.passMark}% pass mark you set${verdict === "borderline" ? ", close enough that this is a judgement call rather than a clear no" : ""}.`;

  const detail = split + margin;

  let fitFor: string | null = null, fitAgainst: string | null = null, nextStep: string | null = null;
  if (codeStrong && wordsWeak) {
    fitFor = "Building features to a plan someone else designed, inside an existing team.";
    fitAgainst = "Designing systems alone, or explaining technical decisions to other people.";
    nextStep = "A short call where you ask them to talk through how they would investigate a problem. That is the exact thing this test could not measure.";
  } else if (wordsStrong && codeWeak) {
    fitFor = "Roles leaning on design and communication, with support on implementation.";
    fitAgainst = "Work where they must deliver production code unsupervised from day one.";
    nextStep = "A paired coding session would show whether the hands-on gap is real or nerves.";
  } else if (strongest && weakest && strongest.pct - weakest.pct >= 40) {
    fitFor = `Work centred on ${strongest.label}, which they clearly know.`;
    fitAgainst = `Anything depending on ${weakest.label}, which this assessment did not find.`;
    nextStep = `A short conversation focused on ${weakest.label} would settle whether the gap is real.`;
  }

  return { verdict, headline, detail, fitFor, fitAgainst, nextStep };
}
